import React from 'react';
import axios from 'axios';
import dayjs, { ManipulateType } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Row,
    Slider,
    Space,
    Segmented,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import { Line } from '@ant-design/plots';
import { EnvironmentOutlined, FieldTimeOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import AreaMap from '../components/Map/AreaMap';
import { useAuth } from '../context/AuthContext';
import {
    getBatteryPercent,
    getStatusSnapshot,
    getUnitStatus,
} from '../components/Operations/deviceStatusSnapshot';
import { isVehicleDevice, type DeviceItem } from '../components/Operations/missionModels';
import '../style/Devices.css';

const { Title, Text } = Typography;
const TELEMETRY_WINDOW_MS = 24 * 60 * 60 * 1000;
const TELEMETRY_SCROLL_STEP_MS = 5 * 60 * 1000;

interface DeviceAssignment {
    id: number;
    assignedAt: string;
    assignedByUser?: {
        id: number;
        email: string;
    };
    assignedToCompany?: {
        id: number;
        name: string;
    };
    deviceLoginMethod?: string | null;
    isActive?: boolean;
}

interface Device {
    id: number;
    label: string;
    codePrefix?: string | null;
    serialNumber?: string | null;
    currentStatus?: string | null;
    lastHeartbeat?: string | null;
    lastLocation?: any;
    statusSnapshot?: Record<string, any> | null;
    statusSnapshotAt?: string | null;
    deviceTypeId?: number | null;
    deviceType?: DeviceItem['deviceType'];
    assignmentHistory?: DeviceAssignment[];
}

interface DeviceUsage {
    count: number | null;
    lastMeasurementAt?: string | null;
    lastMeasurementLocation?: { lat: number; lng: number } | null;
}

interface MapPoint {
    id: number | string;
    lat: number;
    lng: number;
    label?: string;
    selected?: boolean;
}

type UsageRange = 'last_30_days' | 'last_month' | 'last_year' | 'last_5_years';

interface UsageRangeConfig {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
    bucket: 'day' | 'month';
    labelFormat: string;
    keyFormat: string;
    points: number;
    unit: ManipulateType;
}

interface DeviceTelemetryHistoryPoint {
    key: string;
    timestamp: number;
    label: string;
    batteryLevel: number | null;
    batterySamples: number;
    powerOnCount: number;
    powerOffCount: number;
}

interface DeviceTelemetryTotals {
    telemetryRecords: number;
    batterySamples: number;
    powerOnCount: number;
    powerOffCount: number;
}

const getUsageRangeConfig = (usageRange: UsageRange): UsageRangeConfig => {
    if (usageRange === 'last_30_days') {
        const to = dayjs().endOf('day');
        const from = dayjs().subtract(29, 'day').startOf('day');
        return {
            from,
            to,
            bucket: 'day',
            labelFormat: 'MMM D',
            keyFormat: 'YYYY-MM-DD',
            points: 30,
            unit: 'day',
        };
    }
    if (usageRange === 'last_month') {
        const from = dayjs().subtract(1, 'month').startOf('month');
        const to = dayjs().subtract(1, 'month').endOf('month');
        return {
            from,
            to,
            bucket: 'day',
            labelFormat: 'MMM D',
            keyFormat: 'YYYY-MM-DD',
            points: to.diff(from, 'day') + 1,
            unit: 'day',
        };
    }
    if (usageRange === 'last_5_years') {
        const from = dayjs().subtract(59, 'month').startOf('month');
        const to = dayjs().endOf('month');
        return {
            from,
            to,
            bucket: 'month',
            labelFormat: 'MMM YYYY',
            keyFormat: 'YYYY-MM',
            points: to.diff(from, 'month') + 1,
            unit: 'month',
        };
    }
    const from = dayjs().subtract(11, 'month').startOf('month');
    const to = dayjs().endOf('month');
    return {
        from,
        to,
        bucket: 'month',
        labelFormat: 'MMM YYYY',
        keyFormat: 'YYYY-MM',
        points: to.diff(from, 'month') + 1,
        unit: 'month',
    };
};

const formatTelemetryMinute = (value: string | number | Date, timezone: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    try {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(date);
    }
};

const parseLocation = (location: any): { lat: number; lng: number } | null => {
    if (!location) return null;

    if (location.type === 'Point' && Array.isArray(location.coordinates)) {
        const [lng, lat] = location.coordinates;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
            return { lat, lng };
        }
    }

    if (Array.isArray(location.coordinates)) {
        const [lng, lat] = location.coordinates;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
            return { lat, lng };
        }
    }

    if (Number.isFinite(location.lng) && Number.isFinite(location.lat)) {
        return { lat: location.lat, lng: location.lng };
    }

    if (Number.isFinite(location.longitude) && Number.isFinite(location.latitude)) {
        return { lat: location.latitude, lng: location.longitude };
    }

    if (typeof location === 'string') {
        const match = location.match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);
        if (match) {
            const first = parseFloat(match[1]);
            const second = parseFloat(match[2]);
            if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
                return { lat: first, lng: second };
            }
            if (Math.abs(second) <= 90 && Math.abs(first) <= 180) {
                return { lat: second, lng: first };
            }
        }
    }

    return null;
};

const parseMeasurementLocation = (measurement: any): { lat: number; lng: number } | null => {
    const geom = measurement?.geom;
    if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
        const [lng, lat] = geom.coordinates;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
            return { lat, lng };
        }
    }
    return null;
};

const Devices: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [devices, setDevices] = React.useState<Device[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [usageLoading, setUsageLoading] = React.useState(false);
    const [usageByDevice, setUsageByDevice] = React.useState<Record<number, DeviceUsage>>({});
    const [usageSeries, setUsageSeries] = React.useState<Array<{ label: string; count: number }>>([]);
    const [usageSeriesLoading, setUsageSeriesLoading] = React.useState(false);
    const [telemetrySeries, setTelemetrySeries] = React.useState<DeviceTelemetryHistoryPoint[]>([]);
    const [telemetrySeriesLoading, setTelemetrySeriesLoading] = React.useState(false);
    const [telemetrySeriesError, setTelemetrySeriesError] = React.useState('');
    const [telemetryTotals, setTelemetryTotals] = React.useState<DeviceTelemetryTotals>({
        telemetryRecords: 0,
        batterySamples: 0,
        powerOnCount: 0,
        powerOffCount: 0,
    });
    const [telemetryTimezone, setTelemetryTimezone] = React.useState('UTC');
    const [telemetryWindowSelection, setTelemetryWindowSelection] = React.useState<{
        key: string;
        start: number;
    } | null>(null);
    const [selectedDeviceId, setSelectedDeviceId] = React.useState<number | null>(null);
    const [usageRange, setUsageRange] = React.useState<UsageRange>('last_30_days');
    const usageRangeConfig = React.useMemo(() => getUsageRangeConfig(usageRange), [usageRange]);
    const telemetryRange = React.useMemo(() => {
        const start = usageRangeConfig.from.valueOf();
        const end = Math.min(usageRangeConfig.to.valueOf(), Date.now());
        const maxStart = Math.max(start, end - TELEMETRY_WINDOW_MS);
        return { start, end, maxStart };
    }, [usageRangeConfig]);
    const telemetryWindowKey = `${selectedDeviceId || 'none'}:${usageRange}:${telemetryRange.start}:${telemetryRange.end}`;
    const telemetryWindowStart = telemetryWindowSelection?.key === telemetryWindowKey
        ? Math.min(telemetryRange.maxStart, Math.max(telemetryRange.start, telemetryWindowSelection.start))
        : telemetryRange.maxStart;
    const telemetryWindowEnd = Math.min(telemetryRange.end, telemetryWindowStart + TELEMETRY_WINDOW_MS);

    const currentCompanyId = React.useMemo(() => {
        return (
            currentUser?.currentCompanyId ||
            currentUser?.companyId ||
            currentUser?.companyMemberships?.[0]?.company?.id ||
            null
        );
    }, [
        currentUser?.currentCompanyId,
        currentUser?.companyId,
        currentUser?.companyMemberships,
    ]);

    const deviceIdsSignature = React.useMemo(
        () => devices.map((device) => device.id).sort((a, b) => a - b).join(','),
        [devices]
    );
    const deviceIdsForUsage = React.useMemo(
        () => deviceIdsSignature ? deviceIdsSignature.split(',').map((id) => Number(id)).filter(Number.isFinite) : [],
        [deviceIdsSignature]
    );

    const loadDevices = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.GET_DEVICES, {
                params: {
                    assignment_status: currentCompanyId ? 'assigned' : 'mine',
                    company_id: currentCompanyId ?? undefined,
                    include_history: 'true',
                },
                withCredentials: true,
            });
            const payload = response.data?.data || [];
            setDevices(payload);
        } catch (error) {
            console.error('Failed to load devices', error);
        } finally {
            setLoading(false);
        }
    }, [currentCompanyId]);

    React.useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    React.useEffect(() => {
        if (deviceIdsForUsage.length === 0) {
            setUsageByDevice({});
            return;
        }

        let cancelled = false;
        const loadUsage = async () => {
            setUsageLoading(true);
            try {
                const entries = await Promise.all(
                    deviceIdsForUsage.map(async (deviceId) => {
                        try {
                            const response = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                                params: {
                                    device_id: deviceId,
                                    limit: 1,
                                    offset: 0,
                                },
                                withCredentials: true,
                            });
                            const total = Number(response.data?.total ?? 0);
                            const latest = response.data?.data?.[0] || null;
                            const lastMeasurementAt = latest?.measuredAt || latest?.createdAt || null;
                            const lastMeasurementLocation = parseMeasurementLocation(latest);
                            return [deviceId, { count: total, lastMeasurementAt, lastMeasurementLocation }] as const;
                        } catch (err) {
                            return [deviceId, { count: null, lastMeasurementAt: null, lastMeasurementLocation: null }] as const;
                        }
                    })
                );

                if (!cancelled) {
                    const next: Record<number, DeviceUsage> = {};
                    entries.forEach(([deviceId, usage]) => {
                        next[deviceId] = usage;
                    });
                    setUsageByDevice(next);
                }
            } finally {
                if (!cancelled) {
                    setUsageLoading(false);
                }
            }
        };

        loadUsage();

        return () => {
            cancelled = true;
        };
    }, [deviceIdsForUsage]);

    React.useEffect(() => {
        const loadUsageSeries = async () => {
            if (!selectedDeviceId) {
                setUsageSeries([]);
                return;
            }

            setUsageSeriesLoading(true);
            try {
                const response = await axios.get(ENDPOINTS.DEVICE_USAGE(selectedDeviceId), {
                    params: {
                        from_date: usageRangeConfig.from.toISOString(),
                        to_date: usageRangeConfig.to.toISOString(),
                        bucket: usageRangeConfig.bucket,
                    },
                    withCredentials: true,
                });
                const rows = Array.isArray(response.data?.data) ? response.data.data : [];
                const byMonth = new Map<string, number>();
                rows.forEach((row: any) => {
                    const key = typeof row.bucket === 'string' ? row.bucket : null;
                    if (!key) return;
                    byMonth.set(key, Number(row.count || 0));
                });

                const series = Array.from({ length: usageRangeConfig.points }).map((_, index) => {
                    const value = usageRangeConfig.from.add(index, usageRangeConfig.unit);
                    const key = value.format(usageRangeConfig.keyFormat);
                    return {
                        label: value.format(usageRangeConfig.labelFormat),
                        count: byMonth.get(key) ?? 0,
                    };
                });

                setUsageSeries(series);
            } catch (error) {
                console.error('Failed to load device usage series', error);
                setUsageSeries([]);
            } finally {
                setUsageSeriesLoading(false);
            }
        };

        loadUsageSeries();
    }, [selectedDeviceId, usageRangeConfig]);

    React.useEffect(() => {
        const controller = new AbortController();
        const loadTelemetrySeries = async () => {
            if (!selectedDeviceId) {
                setTelemetrySeries([]);
                setTelemetryTotals({ telemetryRecords: 0, batterySamples: 0, powerOnCount: 0, powerOffCount: 0 });
                setTelemetrySeriesError('');
                return;
            }

            setTelemetrySeriesLoading(true);
            try {
                const response = await axios.get(ENDPOINTS.DEVICE_TELEMETRY_HISTORY(selectedDeviceId), {
                    params: {
                        from_date: new Date(telemetryWindowStart).toISOString(),
                        to_date: new Date(telemetryWindowEnd).toISOString(),
                        bucket: 'sample',
                    },
                    withCredentials: true,
                    signal: controller.signal,
                });
                const rows = Array.isArray(response.data?.data) ? response.data.data : [];
                const timezone = response.data?.timezone || 'UTC';
                if (response.data?.bucket !== 'sample') {
                    throw new Error(
                        `The backend returned ${response.data?.bucket || 'unknown'} aggregates instead of battery samples. ` +
                        'Update the development backend before using this chart.'
                    );
                }

                setTelemetrySeries(rows.flatMap((row: any, index: number) => {
                    const timestampText = typeof row.bucketAt === 'string'
                        ? row.bucketAt
                        : typeof row.bucket === 'string'
                            ? row.bucket
                            : `${index}`;
                    const timestamp = Date.parse(timestampText);
                    if (!Number.isFinite(timestamp)) return [];
                    const batteryLevel = row.batteryPercent === null || row.batteryPercent === undefined
                        ? null : Number(row.batteryPercent);
                    return [{
                        key: timestampText,
                        timestamp,
                        label: formatTelemetryMinute(timestampText, timezone),
                        batteryLevel: batteryLevel !== null && Number.isFinite(batteryLevel) ? batteryLevel : null,
                        batterySamples: Number(row.batterySamples || 0),
                        powerOnCount: Number(row.powerOnCount || 0),
                        powerOffCount: Number(row.powerOffCount || 0),
                    }];
                }));
                setTelemetryTotals({
                    telemetryRecords: Number(response.data?.totals?.telemetryRecords || 0),
                    batterySamples: Number(response.data?.totals?.batterySamples || 0),
                    powerOnCount: Number(response.data?.totals?.powerOnCount || 0),
                    powerOffCount: Number(response.data?.totals?.powerOffCount || 0),
                });
                setTelemetryTimezone(timezone);
                setTelemetrySeriesError('');
            } catch (error) {
                if (axios.isCancel(error)) return;
                console.error('Failed to load device telemetry series', error);
                setTelemetrySeries([]);
                setTelemetryTotals({ telemetryRecords: 0, batterySamples: 0, powerOnCount: 0, powerOffCount: 0 });
                const status = axios.isAxiosError(error) ? error.response?.status : null;
                const detail = axios.isAxiosError(error)
                    ? error.response?.data?.message || error.response?.data?.error
                    : error instanceof Error ? error.message : null;
                setTelemetrySeriesError(
                    `Unable to load telemetry history${status ? ` (HTTP ${status})` : ''}${detail ? `: ${detail}` : ''}`
                );
            } finally {
                if (!controller.signal.aborted) setTelemetrySeriesLoading(false);
            }
        };

        loadTelemetrySeries();
        return () => controller.abort();
    }, [selectedDeviceId, telemetryWindowEnd, telemetryWindowStart]);

    const devicePoints = React.useMemo<MapPoint[]>(() => {
        return devices
            .map((device) => {
                const fallback = usageByDevice[device.id]?.lastMeasurementLocation || null;
                const coords = parseLocation(device.lastLocation) || fallback;
                if (!coords) return null;
                return {
                    id: device.id,
                    lat: coords.lat,
                    lng: coords.lng,
                    label: device.label,
                    selected: device.id === selectedDeviceId,
                };
            })
            .filter(Boolean) as MapPoint[];
    }, [devices, selectedDeviceId, usageByDevice]);

    const selectedDevice = React.useMemo(() => {
        return devices.find((device) => device.id === selectedDeviceId) || null;
    }, [devices, selectedDeviceId]);

    const selectedLocation = React.useMemo(() => {
        if (!selectedDevice) return null;
        return parseLocation(selectedDevice.lastLocation) || usageByDevice[selectedDevice.id]?.lastMeasurementLocation || null;
    }, [selectedDevice, usageByDevice]);

    const focusPoint = React.useMemo(() => {
        if (!selectedLocation || !selectedDeviceId) return null;
        return { lat: selectedLocation.lat, lng: selectedLocation.lng, zoom: 15 };
    }, [selectedLocation, selectedDeviceId]);

    const stats = React.useMemo(() => {
        const total = devices.length;
        const online = devices.filter((d) => getUnitStatus(d) === 'online').length;
        const hasLocation = devices.filter((d) => {
            const direct = parseLocation(d.lastLocation);
            const fallback = usageByDevice[d.id]?.lastMeasurementLocation;
            return !!direct || !!fallback;
        }).length;
        const recent = devices.filter((d) => {
            const lastHeartbeat = d.lastHeartbeat ? dayjs(d.lastHeartbeat) : null;
            const lastMeasurementAt = usageByDevice[d.id]?.lastMeasurementAt
                ? dayjs(usageByDevice[d.id]?.lastMeasurementAt)
                : null;
            const candidates = [lastHeartbeat, lastMeasurementAt].filter(Boolean) as dayjs.Dayjs[];
            if (candidates.length === 0) return false;
            const latest = candidates.reduce((acc, cur) => (cur.isAfter(acc) ? cur : acc));
            return latest.isAfter(dayjs().subtract(24, 'hour'));
        }).length;
        const totalMeasurements = Object.values(usageByDevice).reduce((acc, usage) => acc + (usage.count || 0), 0);
        return { total, online, hasLocation, recent, totalMeasurements };
    }, [devices, usageByDevice]);

    const hasMapPoints = devicePoints.length > 0;
    const mapLoading = loading || (usageLoading && !hasMapPoints);

    const usageChartConfig = React.useMemo(() => ({
        data: usageSeries,
        xField: 'label',
        yField: 'count',
        color: '#1677ff',
        smooth: true,
        point: { size: 4, shape: 'circle' },
        tooltip: {
            showMarkers: false,
            shared: false,
            items: [{ channel: 'y', name: 'Measurements' }],
        },
        yAxis: {
            nice: true,
            label: { formatter: (value: string) => `${value}` },
        },
        xAxis: {
            label: { autoHide: true, autoRotate: false },
        },
        height: 240,
    }), [usageSeries]);

    const telemetryHasData = React.useMemo(
        () => telemetrySeries.some((point) => point.batteryLevel !== null),
        [telemetrySeries]
    );

    const latestBattery = React.useMemo(() => {
        for (let index = telemetrySeries.length - 1; index >= 0; index -= 1) {
            if (telemetrySeries[index].batteryLevel !== null) {
                return telemetrySeries[index].batteryLevel;
            }
        }
        return null;
    }, [telemetrySeries]);

    const telemetryChartConfig = React.useMemo(() => {
        const batteryData = telemetrySeries
            .filter((point) => point.batteryLevel !== null)
            .map((point) => ({
                timestamp: point.timestamp,
                label: point.label,
                battery: point.batteryLevel,
            }));

        return {
            data: batteryData,
            xField: 'timestamp',
            yField: 'battery',
            color: '#52c41a',
            height: 280,
            scale: {
                x: {
                    type: 'time',
                    domain: [telemetryWindowStart, telemetryWindowEnd],
                    nice: false,
                },
                y: { domain: [0, 100], nice: false },
            },
            axis: {
                x: {
                    labelAutoHide: true,
                    labelAutoRotate: false,
                    labelFormatter: (value: string | number | Date) => formatTelemetryMinute(value, telemetryTimezone),
                },
                y: { title: 'Battery (%)' },
            },
            style: { stroke: '#52c41a', lineWidth: 2 },
            point: { size: 3, shape: 'circle', tooltip: false },
            tooltip: {
                title: (datum: any) => formatTelemetryMinute(datum?.timestamp, telemetryTimezone),
                items: [
                    (datum: any) => ({
                        name: 'Battery level',
                        value: `${Number(datum?.battery || 0).toFixed(1)}%`,
                        color: '#52c41a',
                    }),
                ],
            },
            interaction: {
                tooltip: { shared: false },
            },
            animation: false,
        };
    }, [telemetrySeries, telemetryTimezone, telemetryWindowEnd, telemetryWindowStart]);

    const usageRangeOptions = [
        { label: 'Last 30 days', value: 'last_30_days' },
        { label: 'Last month', value: 'last_month' },
        { label: 'Last year', value: 'last_year' },
        { label: 'Last 5 years', value: 'last_5_years' },
    ];

    const columns = [
        {
            title: 'Device',
            dataIndex: 'label',
            key: 'label',
            render: (_: any, record: Device) => (
                <Space direction="vertical" size={2} className="devices-device-cell">
                    <Text strong>{record.label}</Text>
                    <Text type="secondary" className="devices-device-meta">
                        {[
                            record.serialNumber ? `SN ${record.serialNumber}` : null,
                            record.deviceType?.name || null,
                        ].filter(Boolean).join(' · ') || 'No serial or type set'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'currentStatus',
            key: 'currentStatus',
            render: (_status: string | null, record: Device) => {
                const normalized = getUnitStatus(record) || 'unknown';
                const battery = getBatteryPercent(getStatusSnapshot(record));
                const color =
                    normalized === 'online'
                        ? 'green'
                        : normalized === 'offline'
                        ? 'red'
                        : normalized === 'maintenance'
                        ? 'orange'
                        : 'default';
                return (
                    <Space direction="vertical" size={2} className="devices-status-cell">
                        <Tag color={color}>{normalized}</Tag>
                        <Text type="secondary" className="devices-device-meta">
                            {battery !== undefined ? `${battery.toFixed(0)}% battery` : 'Battery unknown'}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Last seen',
            dataIndex: 'lastHeartbeat',
            key: 'lastHeartbeat',
            responsive: ['md'],
            render: (value: string | null) =>
                value ? (
                    <Space size={6}>
                        <FieldTimeOutlined />
                        <span>{dayjs(value).format('MMM D, YYYY HH:mm')}</span>
                    </Space>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Measurements',
            dataIndex: 'measurements',
            key: 'measurements',
            render: (_: any, record: Device) => {
                const usage = usageByDevice[record.id];
                if (usageLoading && !usage) {
                    return <Spin size="small" />;
                }
                return <Text>{usage?.count ?? '—'}</Text>;
            },
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: any, record: Device) => {
                if (!isVehicleDevice(record)) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Button
                        type="primary"
                        size="small"
                        className="devices-live-button"
                        icon={<PlayCircleOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/operations/device/${record.id}`);
                        }}
                    >
                        Open live view
                    </Button>
                );
            },
        },
    ];

    return (
        <div className="devices-page">
            <Row justify="space-between" align="middle" className="devices-header">
                <Col>
                    <Space direction="vertical" size={0}>
                        <Title level={2} style={{ margin: 0 }}>Devices</Title>
                        <Text type="secondary" className="devices-subtitle">
                            Assigned devices, last activity, and usage stats.
                        </Text>
                    </Space>
                </Col>
            </Row>

            <div className="devices-stats-grid">
                <Card className="devices-stat-card">
                    <Text type="secondary">Total devices</Text>
                    <Title level={3}>{stats.total}</Title>
                </Card>
                <Card className="devices-stat-card">
                    <Text type="secondary">Online now</Text>
                    <Title level={3}>{stats.online}</Title>
                </Card>
                <Card className="devices-stat-card">
                    <Text type="secondary">Seen in last 24h</Text>
                    <Title level={3}>{stats.recent}</Title>
                </Card>
                <Card className="devices-stat-card">
                    <Text type="secondary">Total measurements</Text>
                    <Title level={3}>{stats.totalMeasurements}</Title>
                </Card>
                <Card className="devices-stat-card">
                    <Text type="secondary">With location</Text>
                    <Title level={3}>{stats.hasLocation}</Title>
                </Card>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={14}>
                    <Card className="devices-table-card">
                        {loading ? (
                            <div className="devices-loading">
                                <Spin tip="Loading devices..." />
                            </div>
                        ) : devices.length === 0 ? (
                            <Empty description="No devices assigned to this company yet." />
                        ) : (
                            <Table
                                className="devices-table"
                                dataSource={devices}
                                columns={columns as any}
                                rowKey={(record) => String(record.id)}
                                pagination={{ pageSize: 8 }}
                                onRow={(record) => ({
                                    onClick: () => setSelectedDeviceId(record.id),
                                    style: { cursor: 'pointer' },
                                })}
                                rowClassName={(record) =>
                                    record.id === selectedDeviceId ? 'devices-row-selected' : ''
                                }
                            />
                        )}
                    </Card>
                    <Card
                        className="devices-usage-card"
                        title="Usage history"
                        extra={
                            <Segmented
                                className="devices-usage-range"
                                options={usageRangeOptions}
                                value={usageRange}
                                onChange={(value) => setUsageRange(value as typeof usageRange)}
                            />
                        }
                    >
                        <Text type="secondary" className="devices-usage-subtitle">
                            {usageRange === 'last_30_days'
                                ? 'Daily measurements (last 30 days, including today)'
                                : usageRange === 'last_month'
                                ? 'Daily measurements (previous calendar month)'
                                : 'Monthly measurements'}
                        </Text>
                        {!selectedDeviceId ? (
                            <Text type="secondary">Select a device to see usage.</Text>
                        ) : usageSeriesLoading ? (
                            <div className="devices-loading">
                                <Spin tip="Loading usage..." />
                            </div>
                        ) : usageSeries.length === 0 ? (
                            <Empty description="No measurements in the last 12 months." />
                        ) : (
                            <Line {...usageChartConfig} />
                        )}
                    </Card>
                    <Card
                        className="devices-usage-card devices-telemetry-card"
                        title="Battery level"
                        extra={<Text type="secondary">Fixed 24-hour window</Text>}
                    >
                        <Text type="secondary" className="devices-usage-subtitle">
                            Battery level at each recorded sample · {telemetryTimezone}
                        </Text>
                        <Space wrap className="devices-telemetry-summary">
                            <Tag color={latestBattery === null ? 'default' : latestBattery > 50 ? 'green' : latestBattery > 20 ? 'gold' : 'red'}>
                                Latest in window: {latestBattery === null ? 'No data' : `${latestBattery.toFixed(1)}%`}
                            </Tag>
                            <Tag>Samples in window: {telemetryTotals.batterySamples}</Tag>
                        </Space>
                        {selectedDeviceId && (
                            <div className="devices-telemetry-window-control">
                                <Text className="devices-telemetry-window-label">
                                    Visible: {formatTelemetryMinute(telemetryWindowStart, telemetryTimezone)} – {formatTelemetryMinute(telemetryWindowEnd, telemetryTimezone)}
                                </Text>
                                <Slider
                                    key={telemetryWindowKey}
                                    min={telemetryRange.start}
                                    max={telemetryRange.maxStart}
                                    step={TELEMETRY_SCROLL_STEP_MS}
                                    defaultValue={telemetryWindowStart}
                                    onChangeComplete={(start) => setTelemetryWindowSelection({
                                        key: telemetryWindowKey,
                                        start: Number(start),
                                    })}
                                    tooltip={{
                                        formatter: (start) => start == null
                                            ? null
                                            : formatTelemetryMinute(start, telemetryTimezone),
                                    }}
                                />
                                <div className="devices-telemetry-window-bounds">
                                    <Text type="secondary">{formatTelemetryMinute(telemetryRange.start, telemetryTimezone)}</Text>
                                    <Text type="secondary">{formatTelemetryMinute(telemetryRange.end, telemetryTimezone)}</Text>
                                </div>
                            </div>
                        )}
                        {!selectedDeviceId ? (
                            <Text type="secondary">Select a device to see battery history.</Text>
                        ) : telemetrySeriesLoading ? (
                            <div className="devices-loading">
                                <Spin tip="Loading telemetry..." />
                            </div>
                        ) : telemetrySeriesError ? (
                            <Alert type="error" showIcon message={telemetrySeriesError} />
                        ) : !telemetryHasData ? (
                            <Empty description="No battery telemetry in this 24-hour window." />
                        ) : (
                            <Line {...telemetryChartConfig as any} />
                        )}
                    </Card>
                </Col>
                <Col xs={24} xl={10}>
                    <Card className="devices-map-card" title="Last known location">
                        <div className="devices-map-wrapper">
                            {mapLoading ? (
                                <div className="devices-map-loading">
                                    <Spin tip="Loading device locations..." />
                                </div>
                            ) : hasMapPoints ? (
                                <AreaMap
                                    points={devicePoints}
                                    mapKey="devices-map"
                                    pointLabelPosition="above"
                                    focusPoint={focusPoint}
                                    onPointClick={(pointId) => {
                                        const id = typeof pointId === 'string' ? parseInt(pointId, 10) : Number(pointId);
                                        if (!Number.isNaN(id)) {
                                            setSelectedDeviceId(id);
                                        }
                                    }}
                                />
                            ) : (
                                <Empty description="No device locations available yet." />
                            )}
                        </div>
                        <div className="devices-map-footer">
                            {selectedDevice ? (
                                <div className="devices-selected">
                                    <Text strong>{selectedDevice.label}</Text>
                                    <Space size={12} wrap>
                                        {selectedDevice.deviceType?.name && (
                                            <Text type="secondary">
                                                Type {selectedDevice.deviceType.name} (#{selectedDevice.deviceType.id})
                                            </Text>
                                        )}
                                        <Text type="secondary">
                                            <EnvironmentOutlined />{' '}
                                            {selectedLocation
                                                ? `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`
                                                : 'No location'}
                                        </Text>
                                        {selectedLocation && !parseLocation(selectedDevice.lastLocation) && (
                                            <Text type="secondary">(from last measurement)</Text>
                                        )}
                                        <Text type="secondary">
                                            <FieldTimeOutlined />{' '}
                                            {selectedDevice.lastHeartbeat
                                                ? dayjs(selectedDevice.lastHeartbeat).format('MMM D, YYYY HH:mm')
                                                : 'No heartbeat'}
                                        </Text>
                                        {isVehicleDevice(selectedDevice) && (
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<PlayCircleOutlined />}
                                                onClick={() => navigate(`/operations/device/${selectedDevice.id}`)}
                                            >
                                                Open live view
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            ) : (
                                <Text type="secondary">Select a device to see details.</Text>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Devices;
